<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      label: string;
      name: string;
      options: Array<Schemas['PropertyGroupOption'] & { count: number }>;
      entities: Array<Schemas['ProductManufacturer'] & { count: number }>;
    }
  "
>
import { getTranslatedProperty } from "@shopware/helpers";
import { inject, computed } from "vue";
import type { Schemas } from "#shopware";
import { useSidebarStore } from "~/stores/sidebar";

const { t, te } = useI18n();
const sidebarStore = useSidebarStore();

const props = defineProps<{
  filter: ListingFilter;
  isFilterVisible: boolean;
  toggle: Function;
}>();

const emits = defineEmits<{
  (event: "select-value", value: { code: string; value: unknown }): void;
  (event: "update-visibility", flag: boolean): void;
}>();

const selectedOptionIds = inject<string[]>("selectedOptionIds");

// Get translated filter label
const filterLabel = computed(() => {
  const translationKey = `filters.${props.filter.code}`;
  // Try to get translation, fallback to filter.label if translation doesn't exist
  if (te(translationKey)) {
    return t(translationKey);
  }
  return props.filter.label;
});
</script>

<template>
  <div 
    :class="{
      'active' : isFilterVisible
    }"
    class="border-b border-gray-200 collapse-item">
    <button
      @click="toggle($event)"
      class="py-4 flex bg-transparent border-0 w-full items-center justify-between text-left cursor-pointer">
      <span class="font-mulish font-semibold text-base 3xl:text-md max-w-[90%] text-grey-700"
      >{{ filterLabel }}</span
      >
      <span class="accordion-icon">
        <i 
        :class="{
          'icon-minus': isFilterVisible,
          'icon-add': !isFilterVisible
        }"
        class="text-black transform transition-transform duration-200"></i>
      </span>
    </button>
    <transition 
      enter-active-class="transition-all duration-300 ease-in-out overflow-hidden"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[240px] opacity-100"
      leave-active-class="transition-all duration-300 ease-in-out overflow-hidden"
      leave-from-class="max-h-[240px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <div 
      v-if="isFilterVisible"
      :id="props.filter.code" 
      class="accordion-content overflow-hidden transition-all max-h-0 duration-300 pr-1">
        <div class="accordian-inner pt-2 pb-6">
          <ul 
            class="max-h-56 list-none overflow-hidden"
            :class="{
              '!overflow-auto custom-scrollbar': props.filter.options?.length > 6 || props.filter.entities?.length > 6
            }"
            >
            <li 
              v-for="option in props.filter.options || props.filter.entities"     
              :key="`${option.id}-${selectedOptionIds?.includes(option.id)}`"
              class="mb-3 last:mb-0">
                <div class="checkbox-wrap relative flex items-center gap-3 w-full">
                  <input
                    :id="`filter-mobile-${props.filter.code}-${option.id}`"
                    :checked="selectedOptionIds?.includes(option.id)"
                    :name="props.filter.name"
                    :value="option.name"
                    :aria-label="`${option.name} filter`"
                    type="checkbox"
                    @change="
                      emits('select-value', {
                        code: props.filter.code,
                        value: option.id,
                      })
                    "
                    class="custom-checkbox peer absolute left-0" />
                  <label
                    :for="`filter-mobile-${props.filter.code}-${option.id}`"
                    class="font-mullish font-normal select-none text-sm lg:text-base peer-checked:text-purple peer-checked:font-semibold text-grey-500 cursor-pointer relative inset-0 z-1 leading-none block w-full "
                    >
                    {{ getTranslatedProperty(option, "name") }}
                  </label>
                </div>
              </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
